<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <nav
    class="side__menu"
    data-testid="side-menu"
    :class="{ hovered }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @keydown.esc="handleEsc"
  >
    <div
      :class="{ side__menu__brand__kern: instanceVendor === 'kern' }"
      class="side__menu__brand flex h-14 w-full shrink-0 items-center justify-center border-b border-[#B8CBEC]"
    >
      <router-link
        :to="HOME_ROUTE"
        class="flex size-full items-center justify-center"
        data-testid="sidebar-home-link"
        aria-label="Home"
        @click="hovered = false"
      >
        <img v-if="logoPath" :src="logoPath" alt="Home" />
        <AppIcon
          v-else
          name="i-heroicons-arrow-path-20-solid"
          class="logo-spinner"
          size="2rem"
        />
      </router-link>
    </div>
    <UserAndLang
      :hovered="hovered"
      @link-clicked="hovered = false"
      @dropdown-open="handleDropdownOpen"
      @dropdown-close="handleDropdownClose"
    />
    <LinksMenu :hovered="hovered" @link-clicked="hovered = false" />
    <Teleport to="body">
      <TrialStatusCard
        v-if="stripeEnabled && !isBillingRoute && isTrialing"
        :sidebar-hovered="hovered"
        :subscription-status="billingStatus"
        :trial-days-remaining="trialDaysRemaining"
        :checkout-loading="checkoutLoading"
        :instance-vendor="instanceVendor"
        @upgrade-click="handleUpgradeClick"
      />
    </Teleport>

    <div class="side__menu__settings">
      <FooterElement :hovered="hovered" />
      <LogoutButton :hovered="hovered" />
    </div>
  </nav>
</template>

<script>
import FooterElement from '~/components/Utilities/FooterElement.vue'
import LinksMenu from '~/components/Utilities/LinksMenu.vue'
import TrialStatusCard from '~/components/MainPage/TrialStatusCard.vue'
import { isTrialingStatus } from '~~/shared/billing/subscription-status'
import {
  BILLING_ROUTE_PREFIX,
  BILLING_SUCCESS_ROUTE,
  HOME_ROUTE,
} from '~~/shared/routes'
import { resolveStripeEnabled } from '~/components/Utilities/billingFeatureFlag'
import { useBillingStatus } from '~/composables/useBillingStatus'
import UserAndLang from '~/components/Utilities/UserAndLang.vue'
import LogoutButton from '~/components/Utilities/LogoutButton.vue'

export default {
  components: {
    FooterElement,
    LinksMenu,
    TrialStatusCard,
    UserAndLang,
    LogoutButton,
  },
  props: {
    logoUrl: {
      type: String,
      default: null,
    },
  },
  setup() {
    const config = useRuntimeConfig()
    return {
      publicConfig: config.public,
      ...useBillingStatus(),
    }
  },
  data() {
    return {
      HOME_ROUTE,
      hovered: false,
      instanceVendor: this.publicConfig?.instanceVendor || 'laniqo',
      dropdownOpen: false,
      checkoutLoading: false,
      stripeEnabled: false,
      /** Last path seen by the route watcher — used to detect post-checkout navigation. */
      lastRoutePathForBilling: '',
    }
  },
  computed: {
    logoPath() {
      return this.logoUrl || ''
    },
    isBillingRoute() {
      return this.$route?.path?.startsWith(BILLING_ROUTE_PREFIX) || false
    },
    isTrialing() {
      return isTrialingStatus(this.billingStatus)
    },
  },
  watch: {
    '$route.fullPath': {
      async handler() {
        const previousPath = this.lastRoutePathForBilling
        const currentPath = this.$route?.path ?? ''
        const forceRefresh = previousPath === BILLING_SUCCESS_ROUTE
        this.lastRoutePathForBilling = currentPath
        await this.refreshBillingStatus({ forceRefresh })
      },
    },
  },
  async created() {
    const defaultLanguage = 'en'
    const savedLanguage = localStorage.getItem('selectedLanguage')
    if (savedLanguage !== null && savedLanguage !== 'undefined') {
      this.$i18n.locale = savedLanguage
    } else {
      localStorage.setItem('selectedLanguage', defaultLanguage)
    }
    const lastTargets = JSON.parse(
      localStorage.getItem('lastSelectedTargets') || '[]'
    )
    if (lastTargets.length === 0) {
      const initialTargets = ['eng', 'deu', 'pol']
      localStorage.setItem(
        'lastSelectedTargets',
        JSON.stringify(initialTargets)
      )
      window.location.reload()
    }

    this.stripeEnabled = resolveStripeEnabled(this.publicConfig)
    this.lastRoutePathForBilling = this.$route?.path ?? ''
    await this.ensureBillingStatusLoaded()

    window.addEventListener('focus', this.handleWindowFocus)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.handleWindowFocus)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  },
  methods: {
    async handleWindowFocus() {
      await this.refreshBillingStatus()
    },
    async handleVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        return
      }

      await this.refreshBillingStatus()
    },
    handleMouseEnter() {
      this.hovered = true
    },
    handleMouseLeave() {
      if (!this.dropdownOpen) {
        this.hovered = false
      }
    },
    handleDropdownOpen() {
      this.dropdownOpen = true
      this.hovered = true
    },
    handleDropdownClose() {
      this.dropdownOpen = false
      setTimeout(() => {
        if (!this.dropdownOpen) {
          this.hovered = false
        }
      }, 150)
    },
    handleFocusIn() {
      this.hovered = true
    },
    handleFocusOut(event) {
      // Only collapse if new focus is outside the side menu
      if (!this.$el.contains(event.relatedTarget)) {
        if (!this.dropdownOpen) {
          this.hovered = false
        }
      }
    },
    handleEsc() {
      this.hovered = false
      this.dropdownOpen = false
    },
    async handleUpgradeClick() {
      if (this.checkoutLoading || !this.$keycloak?.token) {
        return
      }

      this.checkoutLoading = true
      try {
        const response = await createCustomerPortalSessionViaBff(
          this.$keycloak.token
        )
        if (response?.url) {
          window.location.assign(response.url)
        }
      } catch {
        // The global fetch toaster shows the error message for portal failures.
      } finally {
        this.checkoutLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.side__menu {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 5rem;
  background-color: var(--color-neutral-50);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  overflow-y: hidden;
  box-shadow: 0px 0.25rem 0.5rem 0px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transition: width 0.5s ease;

  &.hovered {
    width: 20rem;
    transition: width 0.5s ease;
    overflow-y: auto;
  }

  &__brand {
    transition: transform 0.3s ease;
    margin-left: 0;
    margin-right: auto;

    img {
      width: 100%;
      height: 100%;
      padding: 0.5rem;
      object-fit: contain;
      transition: transform 0.3s ease;
    }

    .logo-spinner {
      color: var(--color-primary-600);
      animation: spin 1s linear infinite;
    }

    &:hover {
      img {
        transform: scale(1, 1.05) translateY(2.5%);
      }
    }
  }

  &__brand__kern {
    margin-top: 0.5rem;
  }
  &__content {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
  &__settings {
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: auto;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  @media (max-width: 1040px) {
    display: none;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
